'use client'

import Link from 'next/link'
import { useState } from 'react'
import { AvisoBox, Icon, LeadCapture } from '@/components/calc'
import { LEAD_SOURCE_PERDAO } from '@/lib/lead-sources'
import { trackPerdaoChecklistRespondido } from '@/lib/analytics'
import { cn } from '@/lib/utils'

type Resposta = 'sim' | 'nao' | 'nao-sei'

interface Pergunta {
  id: 'eraMei' | 'temDivida' | 'temAcao'
  texto: string
  ajuda: string
  opcoes: { valor: Resposta; rotulo: string }[]
}

const PERGUNTAS: Pergunta[] = [
  {
    id: 'eraMei',
    texto: 'Você estava enquadrado como MEI até 13 de outubro de 2026?',
    ajuda: 'Vale mesmo se você já tiver saído do MEI depois — a lei protege quem estava enquadrado nessa data.',
    opcoes: [
      { valor: 'sim', rotulo: 'Sim' },
      { valor: 'nao', rotulo: 'Não' },
    ],
  },
  {
    id: 'temDivida',
    texto: 'Você tem DAS atrasado, dívida ativa ou parcelamento de antes dessa data?',
    ajuda: 'Inclui parcelamentos em andamento ou cancelados e dívidas já cobradas na Justiça.',
    opcoes: [
      { valor: 'sim', rotulo: 'Sim' },
      { valor: 'nao', rotulo: 'Não' },
      { valor: 'nao-sei', rotulo: 'Não sei' },
    ],
  },
  {
    id: 'temAcao',
    texto: 'Você tem algum processo na Justiça contra essa cobrança?',
    ajuda: 'Por exemplo, embargos à execução fiscal ou ação contestando o valor.',
    opcoes: [
      { valor: 'nao', rotulo: 'Não' },
      { valor: 'sim', rotulo: 'Sim' },
      { valor: 'nao-sei', rotulo: 'Não sei' },
    ],
  },
]

type Respostas = Partial<Record<Pergunta['id'], Resposta>>

export function PerdaoClient() {
  const [respostas, setRespostas] = useState<Respostas>({})

  const responder = (id: Pergunta['id'], valor: Resposta) => {
    setRespostas((r) => ({ ...r, [id]: valor }))
    trackPerdaoChecklistRespondido(id, valor)
  }

  // Quem não era MEI não precisa das outras perguntas.
  const visiveis = respostas.eraMei === 'nao' ? PERGUNTAS.slice(0, 1) : PERGUNTAS
  const completo = visiveis.every((p) => respostas[p.id])

  return (
    <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-2 lg:items-start">
      <section className="rounded-2xl border border-line bg-surface p-6 shadow-soft">
        <p className="mb-4 text-[13px] font-semibold uppercase tracking-wide text-brand">
          Checklist · 3 perguntas
        </p>
        <ol className="space-y-6">
          {visiveis.map((p, i) => (
            <li key={p.id}>
              <p className="text-[15.5px] font-semibold leading-snug text-ink">
                {i + 1}. {p.texto}
              </p>
              <p className="mt-1 text-[12.5px] leading-snug text-ink-mute">{p.ajuda}</p>
              <div className="mt-2.5 flex flex-wrap gap-2" role="radiogroup" aria-label={p.texto}>
                {p.opcoes.map((o) => {
                  const ativo = respostas[p.id] === o.valor
                  return (
                    <button
                      key={o.valor}
                      type="button"
                      role="radio"
                      aria-checked={ativo}
                      onClick={() => responder(p.id, o.valor)}
                      className={cn(
                        'inline-flex h-10 items-center gap-1.5 border px-4 text-[14px] font-medium transition-colors',
                        ativo
                          ? 'border-brand bg-brand text-white'
                          : 'border-line bg-surface text-ink hover:border-brand hover:text-brand',
                      )}
                    >
                      {ativo && <Icon name="check" size={13} />}
                      {o.rotulo}
                    </button>
                  )
                })}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="flex flex-col rounded-2xl border border-line bg-bg-alt p-[22px] shadow-soft">
        {!completo ? (
          <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-line bg-surface px-6 py-12 text-center">
            <Icon name="check" size={28} className="mb-3 text-brand" />
            <p className="text-[16px] font-bold text-ink">Responda ao lado</p>
            <p className="mt-1.5 max-w-[34ch] text-[13.5px] leading-relaxed text-ink-mute">
              Em 30 segundos você sabe se tem direito ao perdão e o que fazer agora pra não perder.
            </p>
          </div>
        ) : (
          <Resultado respostas={respostas} />
        )}
      </section>
    </div>
  )
}

function Resultado({ respostas }: { respostas: Respostas }) {
  if (respostas.eraMei === 'nao') {
    return (
      <>
        <Veredito tone="neutro" titulo="O perdão é só para quem era MEI">
          A remissão da Lei Complementar 238/2026 vale para dívidas de quem estava regularmente
          enquadrado como MEI até 13/10/2026. Se você pretende abrir um MEI agora, o teto sobe para
          R$ 110 mil em 2027.
        </Veredito>
        <Link href="/prospera-mei" className="mt-4 text-[14px] font-semibold text-brand hover:underline">
          Ver tudo o que muda pro MEI →
        </Link>
      </>
    )
  }

  const semDivida = respostas.temDivida === 'nao'
  const naoSabe = respostas.temDivida === 'nao-sei'
  const temAcao = respostas.temAcao === 'sim' || respostas.temAcao === 'nao-sei'

  return (
    <>
      {semDivida ? (
        <Veredito tone="ok" titulo="Sem dívida, nada a pedir">
          Ótimo: o perdão só serve pra quem tem débito. O cuidado agora é não criar dívida nova —
          mantenha o DAS em dia todo mês.
        </Veredito>
      ) : (
        <Veredito tone="bom" titulo={naoSabe ? 'Você pode ter direito — confira primeiro' : 'Você provavelmente tem direito ao perdão'}>
          A lei perdoa a parte federal do DAS (o INSS) com juros e multas, constituída até 13/10/2026.
          ICMS e ISS ficam de fora. Mas atenção: <strong>o perdão não é automático</strong>.
        </Veredito>
      )}

      {!semDivida && (
        <ol className="mt-5 space-y-3.5 text-[14px] leading-relaxed text-ink-soft">
          <Passo n={1} titulo="Veja quanto você deve">
            No{' '}
            <a
              href="https://www8.receita.fazenda.gov.br/SimplesNacional/Aplicacoes/ATSPO/pgmei.app/Identificacao"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-brand hover:underline"
            >
              PGMEI
            </a>{' '}
            (débitos ainda na Receita) e no{' '}
            <a
              href="https://www.regularize.pgfn.gov.br/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-brand hover:underline"
            >
              Regularize
            </a>{' '}
            (dívida ativa). Anote os meses em aberto.
          </Passo>
          <Passo n={2} titulo="Pague todo DAS em dia até julho de 2027">
            É a contrapartida da lei: quem atrasa as contribuições dos 6 meses seguintes perde o
            perdão. Na dúvida sobre a contagem, não atrase nenhum a partir de novembro de 2026.
          </Passo>
          <Passo n={3} titulo="Faça o pedido quando a PGFN liberar">
            A baixa depende de requerimento seu. A Procuradoria-Geral da Fazenda Nacional ainda vai
            publicar como pedir.
          </Passo>
          {temAcao && (
            <Passo n={4} titulo="Tem processo? Vai precisar desistir dele">
              O pedido exige comprovar a desistência das ações sobre essas dívidas. Ela fica limitada
              às dívidas perdoadas e não gera honorários só por isso. Fale com seu advogado.
            </Passo>
          )}
        </ol>
      )}

      <AvisoBox tone="amber" icon="alert" className="mt-5">
        Ninguém precisa pagar taxa ou intermediário pelo perdão. O pedido será feito nos canais
        oficiais (gov.br, PGMEI e Regularize). Mensagem cobrando pra &quot;liberar o perdão&quot; é golpe.
      </AvisoBox>

      <LeadCapture
        source={LEAD_SOURCE_PERDAO}
        alertType="das"
        context={{ perdao: { ...respostas } }}
        titulo={
          semDivida
            ? 'Quer o lembrete do DAS todo mês pra nunca criar dívida?'
            : 'Te aviso quando a PGFN liberar o pedido + lembrete de cada DAS até jul/2027?'
        }
        botao="Quero o aviso"
        nota="Grátis · 1 e-mail por mês + o aviso do pedido · cancela quando quiser."
      />
    </>
  )
}

function Veredito({
  tone,
  titulo,
  children,
}: {
  tone: 'bom' | 'ok' | 'neutro'
  titulo: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'border p-4',
        tone === 'bom' && 'border-brand bg-brand-faint',
        tone === 'ok' && 'border-brand-tint-strong bg-surface',
        tone === 'neutro' && 'border-line bg-surface',
      )}
    >
      <p className="text-[17px] font-bold leading-snug text-ink">{titulo}</p>
      <p className="mt-1.5 text-[14px] leading-relaxed text-ink-soft">{children}</p>
    </div>
  )
}

function Passo({ n, titulo, children }: { n: number; titulo: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-brand font-mono text-[12px] font-bold text-white">
        {n}
      </span>
      <div>
        <p className="font-semibold text-ink">{titulo}</p>
        <p>{children}</p>
      </div>
    </li>
  )
}
